// Editable hex value next to a colour field (room controls and picture panel). Accepts #rrggbb, rrggbb and the
// 3-digit shorthand (#rgb, rgb), any case; the value is always shown and sent as lower-case #rrggbb.
// While typing: a complete 6-digit value applies at once ('input'); text that can still become valid is left alone;
// anything else (non-hex characters, too many digits) shows the error state. Enter or blur: a valid value (also the
// shorthand) applies ('commit'); an invalid one goes back to the current colour. Esc is the panels' close key.
// Walking keys and the Shift tap are already ignored while a text input has focus (player/input.ts isTyping).

/** '#rrggbb' (lower case) for #rrggbb, rrggbb, #rgb or rgb (any case, spaces around ignored); else null. */
export function parseHex(text: string): string | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(text.trim());
  if (!m) return null;
  let d = m[1]!.toLowerCase();
  if (d.length === 3) d = d[0]! + d[0]! + d[1]! + d[1]! + d[2]! + d[2]!;
  return `#${d}`;
}

/** Text that may still become a valid value as more is typed. */
const partial = (text: string) => /^#?[0-9a-f]{0,6}$/i.test(text.trim());

export interface HexFieldOptions {
  /** the colour the field shows when it is not being edited */
  current(): string;
  /** a valid colour was typed ('input': while typing, 6 digits; 'commit': Enter or blur) */
  apply(hex: string, mode: 'input' | 'commit'): void;
}

export function hexInput(attrs: { id?: string; label: string; className?: string }): HTMLInputElement {
  const el = document.createElement('input');
  el.type = 'text';
  el.className = `hex-field${attrs.className ? ` ${attrs.className}` : ''}`;
  if (attrs.id) el.id = attrs.id;
  el.setAttribute('aria-label', attrs.label);
  el.setAttribute('autocomplete', 'off');
  el.setAttribute('autocapitalize', 'off');
  el.setAttribute('autocorrect', 'off');
  el.spellcheck = false;
  el.maxLength = 9;
  el.inputMode = 'text';
  el.value = '#ffffff';
  return el;
}

export function bindHexField(el: HTMLInputElement, o: HexFieldOptions): { show(hex: string): void } {
  const setError = (on: boolean) => {
    el.classList.toggle('is-invalid', on);
    if (on) el.setAttribute('aria-invalid', 'true');
    else el.removeAttribute('aria-invalid');
  };
  const revert = () => { el.value = o.current(); setError(false); };
  const commit = () => {
    const hex = parseHex(el.value);
    if (!hex) { revert(); return; }
    el.value = hex;
    setError(false);
    if (hex !== o.current()) o.apply(hex, 'commit');
  };
  el.addEventListener('input', () => {
    const t = el.value.trim();
    if (/^#?[0-9a-f]{6}$/i.test(t)) { setError(false); o.apply(parseHex(t)!, 'input'); return; }
    setError(!partial(t));
  });
  el.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); el.select(); }
  });
  el.addEventListener('blur', commit);
  el.addEventListener('focus', () => el.select());
  return {
    /** Show a colour (an incoming change); not while the field is being edited. */
    show(hex: string) {
      if (document.activeElement === el) return;
      el.value = hex;
      setError(false);
    },
  };
}
